import { useState, useRef, useCallback } from 'react';
import { curationPromptApplicationStream } from '../../../../services/curation_prompt_application.service';
import {
    CPA_INITIAL_UI_STATE,
    applyCpaMarkers,
    extractCpaMarkers,
    stripCpaMarkers,
} from '../utils/cpa.utils';
import {
    CPA_SESSION_MAX_AGE_MS,
    CPA_SESSION_STORAGE_KEY,
    CPA_SESSION_UPDATED_AT_KEY,
} from '../constants/cpa.constants';
import { CHAT_TYPE } from '../../../../utils/constants';

export function useCurationApplicationStream() {
    const [chatHistory, setChatHistory] = useState([]);
    const [uiState, setUiState] = useState(CPA_INITIAL_UI_STATE);
    const [isStreaming, setIsStreaming] = useState(false);
    const uiStateRef = useRef(uiState);
    uiStateRef.current = uiState;

    const clearPersistedSession = useCallback(() => {
        localStorage.removeItem(CPA_SESSION_STORAGE_KEY);
        localStorage.removeItem(CPA_SESSION_UPDATED_AT_KEY);
    }, []);

    // The stream buffer is cumulative, so we SET the streaming bubble's full text
    // (not append). An empty `text` is ignored so marker-only responses (e.g.
    // validation errors) never spawn a blank bot bubble.
    const setBotText = useCallback((text) => {
        if (!text) return;
        setChatHistory((prev) => {
            const last = prev[prev.length - 1];
            if (last && last.type === CHAT_TYPE.BOT && last.streaming) {
                return [...prev.slice(0, -1), { ...last, text, streaming: true }];
            }
            return [...prev, { type: CHAT_TYPE.BOT, text, streaming: true }];
        });
    }, []);

    const finalizeBotBubble = useCallback(() => {
        setChatHistory((prev) => {
            const last = prev[prev.length - 1];
            if (last && last.type === CHAT_TYPE.BOT && last.streaming) {
                return [...prev.slice(0, -1), { ...last, streaming: false }];
            }
            return prev;
        });
    }, []);

    const pushUserBubble = useCallback((text) => {
        setChatHistory((prev) => [...prev, { type: CHAT_TYPE.USER, text }]);
    }, []);

    const runStream = useCallback(async (payload, { userLabel } = {}) => {
        setIsStreaming(true);
        setUiState((s) => ({ ...s, errorCode: null }));
        if (userLabel) pushUserBubble(userLabel);

        try {
            const response = await curationPromptApplicationStream(payload);
            if (!response.ok) {
                setUiState((s) => ({ ...s, errorCode: 'SERVER_ERROR' }));
                return;
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder('utf-8');

            // Server wire format: AI text tokens are written as `data: <token>` with
            // NO trailing newline (so they glue together), while control markers and
            // [DONE] are written as `data: <payload>\n\n`. We therefore split each
            // chunk on the `data: ` delimiter and accumulate everything into one
            // growing buffer — markers are extracted via regex, then stripped from the
            // visible text. (Mirrors the DQProV2 SSE pattern this flow still uses.)
            let rawBuffer = '';
            let markersApplied = 0;
            let doneStreaming = false;

            const flush = () => {
                const markers = extractCpaMarkers(rawBuffer);
                if (markers.length > markersApplied) {
                    const pending = {};
                    for (let i = markersApplied; i < markers.length; i += 1) {
                        pending[markers[i].key] = markers[i].value;
                    }
                    markersApplied = markers.length;
                    setUiState((prev) => {
                        const next = applyCpaMarkers(prev, pending);
                        if (pending.session_id) {
                            localStorage.setItem(CPA_SESSION_STORAGE_KEY, pending.session_id);
                            localStorage.setItem(CPA_SESSION_UPDATED_AT_KEY, String(Date.now()));
                        }
                        if (
                            pending.application_submitted === 'true'
                            || pending.application_complete === 'true'
                            || pending.application_already_submitted === 'true'
                        ) {
                            clearPersistedSession();
                        }
                        return next;
                    });
                }
                setBotText(stripCpaMarkers(rawBuffer));
            };

            while (!doneStreaming) {
                const { done, value } = await reader.read();
                if (done) break;
                const chunk = decoder.decode(value, { stream: true });

                for (const segment of chunk.split('data: ')) {
                    if (segment === '') continue;
                    if (segment.trim() === '[DONE]') { doneStreaming = true; break; }
                    rawBuffer += segment;
                }
                flush();
            }

            flush();
            finalizeBotBubble();
        } catch (e) {
            console.error('[useCurationApplicationStream]', e);
            setUiState((s) => ({ ...s, errorCode: 'SERVER_ERROR' }));
        } finally {
            setIsStreaming(false);
        }
    }, [setBotText, finalizeBotBubble, pushUserBubble]);

    const initSession = useCallback(() => {
        const saved = localStorage.getItem(CPA_SESSION_STORAGE_KEY);
        const updatedAtRaw = localStorage.getItem(CPA_SESSION_UPDATED_AT_KEY);
        const updatedAt = Number(updatedAtRaw || 0);
        const isExpired = !updatedAt || (Date.now() - updatedAt > CPA_SESSION_MAX_AGE_MS);
        const activeSessionId = saved && !isExpired ? saved : null;

        if (saved && isExpired) clearPersistedSession();

        return runStream({
            step: 'init',
            ...(activeSessionId ? { session_id: activeSessionId } : {}),
        });
    }, [runStream, clearPersistedSession]);

    // Start a brand-new enquiry: drop the persisted session id, clear the
    // transcript + UI state, and open a fresh wizard (the server mints a new id).
    const resetSession = useCallback(() => {
        clearPersistedSession();
        setChatHistory([]);
        setUiState(CPA_INITIAL_UI_STATE);
        return runStream({ step: 'init' });
    }, [runStream, clearPersistedSession]);

    // End the current session and clear local cache/state.
    // Used when user explicitly abandons the enquiry flow.
    const abandonSession = useCallback(async () => {
        const sessionId = uiStateRef.current.sessionId || localStorage.getItem(CPA_SESSION_STORAGE_KEY);
        try {
            if (sessionId) {
                await curationPromptApplicationStream({ session_id: sessionId, step: 'abandon' });
            }
        } catch (e) {
            // Best-effort abandon; still clear local state.
        } finally {
            clearPersistedSession();
            setChatHistory([]);
            setUiState(CPA_INITIAL_UI_STATE);
            setIsStreaming(false);
        }
    }, [clearPersistedSession]);

    const submitOrgType = useCallback((form_data, label) => {
        const sessionId = uiStateRef.current.sessionId || localStorage.getItem(CPA_SESSION_STORAGE_KEY);
        return runStream({ session_id: sessionId, step: 'org_type', form_data }, { userLabel: label });
    }, [runStream]);

    const submitGoals = useCallback((form_data, label) => {
        const sessionId = uiStateRef.current.sessionId || localStorage.getItem(CPA_SESSION_STORAGE_KEY);
        return runStream({ session_id: sessionId, step: 'goals', form_data }, { userLabel: label });
    }, [runStream]);

    const submitQuoteResponse = useCallback((wants_quote, label) => {
        const sessionId = uiStateRef.current.sessionId || localStorage.getItem(CPA_SESSION_STORAGE_KEY);
        return runStream(
            { session_id: sessionId, step: 'quote_response', form_data: { wants_quote } },
            { userLabel: label }
        );
    }, [runStream]);

    const submitContactForm = useCallback((form_data, label) => {
        const sessionId = uiStateRef.current.sessionId || localStorage.getItem(CPA_SESSION_STORAGE_KEY);
        return runStream({ session_id: sessionId, step: 'contact_form', form_data }, { userLabel: label });
    }, [runStream]);

    // Free-text question → AI answer. Does not advance the structured flow.
    const sendChatMessage = useCallback((message) => {
        const text = (message || '').trim();
        if (!text) return undefined;
        const sessionId = uiStateRef.current.sessionId || localStorage.getItem(CPA_SESSION_STORAGE_KEY);
        if (!sessionId) return undefined;
        return runStream(
            { session_id: sessionId, step: 'chat_message', form_data: { message: text } },
            { userLabel: text }
        );
    }, [runStream]);

    return {
        chatHistory,
        uiState,
        isStreaming,
        initSession,
        resetSession,
        abandonSession,
        submitOrgType,
        submitGoals,
        submitQuoteResponse,
        submitContactForm,
        sendChatMessage,
    };
}
